import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useUnreadTicketReplies } from '@/hooks/useUnreadTicketReplies';
import { cn } from '@/lib/utils';
import {
  Search,
  LayoutDashboard,
  Users,
  Building2,
  FileSearch,
  TrendingUp,
  Database,
  CreditCard,
  Tag,
  BarChart3,
  HelpCircle,
  Ticket,
  BookOpen,
  Activity,
  Brain,
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import NotificationBell from '@/components/NotificationBell';
import { getTierByProductId } from '@/lib/subscriptions';

export default function Sidebar() {
  const { role } = useAuth();
  const { productId, isAdmin } = useSubscription();
  const { hasUnreadReplies } = useUnreadTicketReplies();
  const { t } = useTranslation();

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), roles: ['admin', 'super_user', 'user'], key: 'dashboard' },
    { to: '/search', icon: Search, label: t('nav.search'), roles: ['admin', 'super_user', 'user'], key: 'search' },
    { to: '/monitoring', icon: FileSearch, label: t('nav.monitoring'), roles: ['admin', 'super_user', 'user'], key: 'monitoring' },
    { to: '/results', icon: TrendingUp, label: t('sidebar.resultsHistory'), roles: ['admin', 'super_user', 'user'], key: 'results' },
    { to: '/data-analysis', icon: Brain, label: t('dataAnalysis.title', 'Data Analysis'), roles: ['admin', 'super_user', 'user'], key: 'data-analysis' },
    { to: '/cpv-search', icon: BookOpen, label: t('sidebar.cpvSearch'), roles: ['admin', 'super_user', 'user'], key: 'cpv-search' },
    { to: '/support', icon: HelpCircle, label: t('sidebar.support'), roles: ['admin', 'super_user', 'user'], key: 'support' },
  ];

  const adminItems = [
    { to: '/admin/client-search', icon: Search, label: t('sidebar.clientSearch'), roles: ['admin'], key: 'admin-client-search' },
    { to: '/clients', icon: Building2, label: t('nav.clients'), roles: ['admin'], key: 'clients' },
    { to: '/users', icon: Users, label: t('nav.users'), roles: ['admin', 'super_user'], key: 'users' },
    { to: '/admin/tickets', icon: Ticket, label: t('sidebar.supportTickets'), roles: ['admin'], key: 'admin-tickets' },
    { to: '/admin/usage-stats', icon: BarChart3, label: t('sidebar.usageStats'), roles: ['admin'], key: 'usage-stats' },
    { to: '/admin/activity', icon: Activity, label: t('sidebar.activityMonitoring', 'Monitoraggio Attività'), roles: ['admin'], key: 'activity-monitoring' },
    { to: '/admin/cpv', icon: Database, label: t('sidebar.cpvMultilingual'), roles: ['admin'], key: 'cpv' },
    { to: '/pricing', icon: CreditCard, label: t('sidebar.plansAndPricing'), roles: ['admin'], key: 'pricing' },
    { to: '/releases', icon: Tag, label: t('sidebar.releaseNotes'), roles: ['admin'], key: 'releases' },
  ];

  const getSubscriptionLabel = (): string => {
    if (isAdmin) return t('sidebar.administrator');
    if (!productId) return 'Free';
    
    const tierKey = getTierByProductId(productId);
    if (!tierKey) return 'Free';
    
    const tierLabels: Record<string, string> = {
      nazionaleBase: t('sidebar.nationalUser'),
      nazionaleProfessional: t('sidebar.nationalProfessionalUser'),
      europaBase: t('sidebar.europeUser'),
      europaProfessional: t('sidebar.europeProfessionalUser'),
    };
    
    return tierLabels[tierKey] || 'Free';
  };

  const filterByRole = (items: typeof navItems) => {
    return items.filter(item => role && item.roles.includes(role));
  };

  return (
    <aside className="w-64 h-screen bg-sidebar flex flex-col border-r border-sidebar-border overflow-hidden">
      {/* Logo */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo-mark.png" 
              alt="TED Monitor" 
              className="w-12 h-12 rounded-xl object-contain"
            />
            <div>
              <h1 className="text-lg font-bold text-sidebar-foreground whitespace-nowrap">TED Monitor</h1>
              <p className="text-sm text-sidebar-foreground/60">{getSubscriptionLabel()}</p>
            </div>
          </div>
          <NotificationBell />
        </div>
      </div>

      <Separator className="bg-sidebar-border" />

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filterByRole(navItems).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-primary'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="flex-1">{item.label}</span>
            {item.key === 'support' && hasUnreadReplies && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
              </span>
            )}
          </NavLink>
        ))}

        {filterByRole(adminItems).length > 0 && (
          <>
            <Separator className="my-4 bg-sidebar-border" />
            <p className="px-3 text-xs font-semibold text-sidebar-foreground/40 uppercase tracking-wider mb-2">
              {t('nav.administration')}
            </p>
            {filterByRole(adminItems).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-primary'
                      : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
                  )
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
    </aside>
  );
}
